(() => {
  const clamp01 = (value) => Math.max(0, Math.min(1, value));
  // Soft ease-in: starts slow but still progresses noticeably.
  // Mixes linear + quadratic so it doesn't feel stuck off-screen.
  const easeInSoft = (t) => t * t * 0.7 + t * 0.3;

  /** @type {Array<{ host: HTMLElement, left?: HTMLElement|null, right?: HTMLElement|null, scrollDistance: number, animationMode: 'exit' | 'reveal', leftOffset: number, rightOffset: number, leftMultiplier: number, rightMultiplier: number }>} */
  const instances = [];

  const parseScrollDistance = (host) => {
    const raw = host.getAttribute('data-pdp-bg-scroll-distance');
    const numberValue = raw ? Number(raw) : NaN;
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 900;
  };

  const getTravelDistance = (host) => {
    // Use section width so it reliably moves off-screen on all breakpoints.
    const width = Math.max(1, host.clientWidth || host.getBoundingClientRect().width || 1);
    return Math.max(320, Math.min(900, width * 0.65));
  };

  const getHideDistance = (host, graphicEl) => {
    // Ensure the graphic starts fully off-screen in reveal mode, regardless of multiplier/offset.
    const graphicWidth = Math.max(1, graphicEl?.getBoundingClientRect().width || 1);
    const hostWidth = Math.max(1, host.clientWidth || host.getBoundingClientRect().width || 1);
    // Cap to avoid absurd translations on huge screens.
    return Math.max(graphicWidth + 64, Math.min(hostWidth, graphicWidth + 220));
  };

  const setupHost = (host) => {
    if (!(host instanceof HTMLElement)) return;
    if (!host.hasAttribute('data-pdp-bg-graphics')) return;

    const left = host.querySelector('.pdp-bg-graphic--left');
    const right = host.querySelector('.pdp-bg-graphic--right');
    if (!left && !right) return;

    // Avoid double-binding (theme editor section reloads).
    if (host.dataset.pdpBgGraphicsInit === 'true') return;
    host.dataset.pdpBgGraphicsInit = 'true';

    const parseNumberAttr = (name, fallback) => {
      const raw = host.getAttribute(name);
      const parsed = raw == null ? NaN : Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    instances.push({
      host,
      left,
      right,
      scrollDistance: parseScrollDistance(host),
      animationMode: host.getAttribute('data-pdp-bg-animation') === 'reveal' ? 'reveal' : 'exit',
      leftOffset: parseNumberAttr('data-pdp-bg-left-offset', 0),
      rightOffset: parseNumberAttr('data-pdp-bg-right-offset', 0),
      leftMultiplier: parseNumberAttr('data-pdp-bg-left-multiplier', 100),
      rightMultiplier: parseNumberAttr('data-pdp-bg-right-multiplier', 100),
    });
  };

  const setupAll = () => {
    document.querySelectorAll('product-info[data-pdp-bg-graphics]').forEach((el) => setupHost(el));
  };

  let rafId = 0;
  const scheduleUpdate = () => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      for (const instance of instances) {
        const rect = instance.host.getBoundingClientRect();

        // Progress starts at 0 on load; as you scroll down and the section moves up,
        // rect.top becomes negative and progress approaches 1.
        const progress = clamp01((0 - rect.top) / instance.scrollDistance);
        const t = easeInSoft(progress);

        const baseDistance = getTravelDistance(instance.host);
        const leftMultiplier = Number.isFinite(instance.leftMultiplier) ? instance.leftMultiplier : 100;
        const rightMultiplier = Number.isFinite(instance.rightMultiplier) ? instance.rightMultiplier : 100;

        const visibleLeft = instance.leftOffset || 0;
        const visibleRight = instance.rightOffset || 0;

        // Travel based on user multiplier, but ensure reveal starts hidden.
        const leftTravelBase = baseDistance * (leftMultiplier / 100);
        const rightTravelBase = baseDistance * (rightMultiplier / 100);
        const leftTravel =
          instance.animationMode === 'reveal' && instance.left
            ? Math.max(leftTravelBase, getHideDistance(instance.host, instance.left))
            : leftTravelBase;
        const rightTravel =
          instance.animationMode === 'reveal' && instance.right
            ? Math.max(rightTravelBase, getHideDistance(instance.host, instance.right))
            : rightTravelBase;

        const hiddenLeft = -leftTravel;
        const hiddenRight = rightTravel;

        const leftX =
          instance.animationMode === 'reveal'
            ? hiddenLeft + (visibleLeft - hiddenLeft) * t
            : visibleLeft + (hiddenLeft - visibleLeft) * t;
        const rightX =
          instance.animationMode === 'reveal'
            ? hiddenRight + (visibleRight - hiddenRight) * t
            : visibleRight + (hiddenRight - visibleRight) * t;

        if (instance.left) instance.left.style.setProperty('--pdp-bg-x', `${leftX}px`);
        if (instance.right) instance.right.style.setProperty('--pdp-bg-x', `${rightX}px`);
      }
    });
  };

  const init = () => {
    setupAll();
    scheduleUpdate();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    // Theme editor: section can be re-rendered.
    document.addEventListener('shopify:section:load', () => {
      setupAll();
      scheduleUpdate();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
